import type { BackofficeUser } from "./types";
import { fetchActiveFlagKeys, fetchEvaluateFlags } from "./api";
import type { FlagKey, FlagRecord } from "./flags";

type InitializeOptions = {
  /** An active-flag request started earlier. A failure here falls back to a fresh request. */
  activeFlagKeysRequest?: Promise<FlagKey[]>;
  signal?: AbortSignal;
};

const loadActiveFlagKeys = async (activeFlagKeysRequest?: Promise<FlagKey[]>, signal?: AbortSignal) => {
  if (activeFlagKeysRequest) {
    try {
      return await activeFlagKeysRequest;
    } catch (error) {
      if (signal?.aborted) {
        throw error;
      }
      // The prefetch was speculative, so a failure gets one ordinary request whose failure is reported below.
    }
  }
  return fetchActiveFlagKeys(signal);
};

const initializeFeatures = async (
  me: BackofficeUser,
  { activeFlagKeysRequest, signal }: InitializeOptions = {}
): Promise<FlagRecord | undefined> => {
  try {
    const activeFlagKeys = await loadActiveFlagKeys(activeFlagKeysRequest, signal);
    if (signal?.aborted) {
      return;
    }
    if (activeFlagKeys.length > 0) {
      return await fetchEvaluateFlags(activeFlagKeys, me, signal);
    }
  } catch (error) {
    if (signal?.aborted) {
      return;
    }
    console.error("Failed to fetch feature flags", error);
  }
};

export default initializeFeatures;
